import React from 'react';
import { useEffect, useState } from 'react';
import { Modal, Typography, Row, Col } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { CustomGridTable } from '../../../blueplanitUtils/CustomGridTable';
import dayjs from 'dayjs';

const { Title } = Typography;

const FinanceWorkHistory = ({ open, onClose, selectedRecord }) => {

  const [attachmentHistoryList, setAttachmentHistoryList] = useState([]);

  useEffect(() => {
    (() => {
      const allHistory = selectedRecord?.attachments
        .filter(att => Array.isArray(att.attachmetHistory))
        .flatMap(att =>
          att.attachmetHistory.map(h => {
            const addDate = h.addDate ? Number(h.addDate) : null;
            const updateDate = h.updateDate ? Number(h.updateDate) : null;
            const deletedDate = h.deletedDate ? Number(h.deletedDate) : null;

            let action = 'Updated';
            let date = updateDate;

            if (deletedDate) {
              action = 'Deleted';
              date = deletedDate;
            } else if (addDate && !updateDate) {
              action = 'Created';
              date = addDate;
            }

            return {
              action,
              date,
              editedBy: h.userName,
              changes: [
                {
                  field: 'attachment',
                  oldValue: h.replacedFile,
                  newValue: h.filename,
                  fileUrl: h.fileUrl,
                },
              ],
            };
          })
        ).sort((a, b) => b.date - a.date);

      setAttachmentHistoryList(allHistory);
    })()
  }, [selectedRecord?.attachments]);


  const changedFields = [
    'date', 'category', 'subCategory', 'purchaseDetails', 'invoice',
    'quantity', 'amount', 'spentBy', 'transactionMode', 'remarks'
  ];
  const enrichedHistory = (selectedRecord?.updateHistory || []).map(h => {
    const editedAt = h.editedAt ? Number(h.editedAt) : null;

    // Derive action from changes
    let action = 'Updated';
    if (h.changes?.some(c => c.newValue === 'Created')) {
      action = 'Created';
    } else if (h.changes?.some(c => c.newValue === 'Deleted')) {
      action = 'Deleted';
    }

    return {
      ...h,
      editedAt,
      action,
    };
  }).sort((a, b) => b.editedAt - a.editedAt);

  const formatValue = (value) => {
    if (typeof value === 'string' && value.length > 40) {
      return `${value.substring(0, 40)}...`;
    }
    return value || '—';
  };
  const assignToUsers = useSelector((state) => state.loginUserInfo.allUsers)

  const getUserName = (editedBy) => {
    if (!editedBy) return 'Anonymous';

    if (typeof editedBy === 'string' && editedBy.includes(' ')) {
      return editedBy;
    }

    const user = assignToUsers?.find((u) => u?.id === editedBy);
    return user ? `${user.firstname} ${user.lastname}` : `${editedBy}`;
  };


  const getStatus = (addDate, updateDate, deleteDate) => {
    const add = addDate ? Number(addDate) : null;
    const update = updateDate ? Number(updateDate) : null;
    const deleted = deleteDate ? Number(deleteDate) : null;

    if (deleted) return 'Deleted';

    // If only addDate exists or updateDate equals addDate, treat as Created
    if (add && (!update || update === add)) return 'Created';

    // If updateDate is after addDate, treat as Updated
    if (update && add && update > add) return 'Updated';

    return '—';
  };

  const workHistoryColumns = [
    {
      title: 'User Name',
      key: 'editedBy',
      width: 150,
      render: (_, record) => getUserName(record.editedBy),
    },
    {
      title: 'Updated Time',
      key: 'editedAt',
      width: 180,
      render: (_, record) => {
        const updateDate = record.updateDate || record.editedAt || record.addDate || record.deletedAt;
        return updateDate ? dayjs(updateDate).format('DD/MM/YYYY hh:mm A') : '—';
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => record.action || getStatus(record.addDate, record.updateDate || record.editedAt, record.deletedDate)
    },


    ...changedFields.map((field) => ({
      title: field.charAt(0).toUpperCase() + field.slice(1),
      key: field,
      width: 160,
      render: (_, record) => {
        const change = record?.changes?.find((c) => c.field === field);
        if (!change || change.oldValue === change.newValue || change.oldValue === 'No change') {
          return <span>No Change</span>;
        }
        return (
          <div>
            <span>{formatValue(change.oldValue)}</span>
            <span style={{ margin: '0 4px' }}>→</span>
            <span>{formatValue(change.newValue)}</span>
          </div>
        );
      },
    }))

  ];

  const attachmentHistoryColumns = [
    {
      title: 'User Name',
      key: 'editedBy',
      width: 150,
      render: (_, record) => getUserName(record.editedBy),
    },
    {
      title: 'Date',
      key: 'date',
      width: 180,
      render: (_, record) => dayjs(record.date).format('DD/MM/YYYY hh:mm A'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => record.action,
    },
    {
      title: 'Attachment Change',
      key: 'attachmentChange',
      render: (_, record) => {
        const attachmentChanges = record.changes || [];
        return (
          <ul style={{ paddingLeft: 16 }}>
            {attachmentChanges.map((att, i) => (
              <li key={i}>
                {att.oldValue} →{' '}
                <a href={att.fileUrl} target="_blank" rel="noreferrer">
                  {att.newValue}
                </a>
              </li>
            ))}
          </ul>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      title="Finance Record Details"
      footer={null}
      onCancel={onClose}
      width="85%"
      style={{ top: 90 }}
    >
      {selectedRecord && (
        <div style={{ marginBottom: 32, padding: 16, background: '#f9f9f9', borderRadius: 8 }}>

          <Row gutter={[16, 12]}>
            <Col span={6}><strong>Date:</strong></Col>
            <Col span={6}>{moment(selectedRecord.date).format('DD/MM/YYYY')}</Col>

            <Col span={6}><strong>Category:</strong></Col>
            <Col span={6}>{selectedRecord.category}</Col>

            <Col span={6}><strong>Sub Category:</strong></Col>
            <Col span={6}>{selectedRecord.subCategory}</Col>

            <Col span={6}><strong>Purchase Details:</strong></Col>
            <Col span={6}>{selectedRecord.purchaseDetails}</Col>

            <Col span={6}><strong>Invoice Number:</strong></Col>
            <Col span={6}>{selectedRecord.invoice}</Col>

            <Col span={6}><strong>Quantity:</strong></Col>
            <Col span={6}>{selectedRecord.quantity}</Col>

            <Col span={6}><strong>Amount:</strong></Col>
            <Col span={6}>{selectedRecord.amount}</Col>

            <Col span={6}><strong>Spent By:</strong></Col>
            <Col span={6}>{selectedRecord.spentBy}</Col>

            <Col span={6}><strong>Transaction Mode:</strong></Col>
            <Col span={6}>{selectedRecord.transactionMode}</Col>

            <Col span={6}><strong>Remarks:</strong></Col>
            <Col span={6}>{selectedRecord.remarks}</Col>
          </Row>
        </div>
      )}

      <div className='mb-3'>
        <Title level={4} style={{ marginBottom: 16 }}>Update History</Title>
        <CustomGridTable
          dataSource={enrichedHistory}
          columns={workHistoryColumns}
          Id="finance-work-history-table"
          rowKey={(record, index) => index}
          pagination={{ pageSize: 5 }}
          scroll={{ x: 'max-content' }}
          bordered
          size="small"
          locale={{ emptyText: 'No update history found' }}
        />
      </div>
      <Title level={4} style={{ marginBottom: 16 }}>Attachments History</Title>
      <CustomGridTable
        dataSource={attachmentHistoryList}
        columns={attachmentHistoryColumns}
        Id="finance-attachment-history-table"
        rowKey={(record, index) => index}
        pagination={{ pageSize: 5 }}
        scroll={{ x: 'max-content' }}
        bordered
        size="small"
        locale={{ emptyText: 'No attachments found' }}
      />

    </Modal>
  );
};

export default FinanceWorkHistory;
